import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import PageHero from "../components/PageHero";
import {
  CheckCircle2, ArrowRight, FileCheck2, BookOpenCheck, ShieldCheck, Layers,
  ClipboardCheck, SearchCheck, Wrench, CalendarCheck, FileText,
} from "lucide-react";
import { services, images } from "../mock";

const iconMap = {
  FileCheck2, BookOpenCheck, ShieldCheck, LayersIcon: Layers, ClipboardCheck,
  SearchCheck, Wrench, CalendarCheck,
};

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = services.find((s) => s.slug === slug);
  if (!service) return <Navigate to="/services" replace />;
  const Icon = iconMap[service.icon] || FileText;
  const related = services.filter((s) => s.slug !== slug).slice(0, 4);

  return (
    <main>
      <PageHero
        eyebrow="Services"
        title={service.title}
        subtitle={service.short}
        breadcrumb={[{ to: "/services", label: "Services" }, { label: service.title }]}
      />

      <section className="py-20">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 rounded-2xl bg-teal-soft text-teal flex items-center justify-center">
                <Icon size={26} />
              </div>
              <div>
                <span className="text-xs font-bold uppercase tracking-[0.18em] text-teal">Service</span>
                <h2 className="text-2xl font-bold text-navy">{service.title}</h2>
              </div>
            </div>

            <p className="text-[16px] text-slate-700 leading-relaxed">{service.description}</p>

            <h3 className="mt-10 text-xl font-bold text-navy">What is included</h3>
            <span className="teal-divider mt-3" />
            <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {service.bullets.map((b) => (
                <li key={b} className="flex items-start gap-3 p-4 bg-white rounded-xl border border-slate-200">
                  <CheckCircle2 size={18} className="text-teal mt-0.5 shrink-0" />
                  <span className="text-sm text-navy/90">{b}</span>
                </li>
              ))}
            </ul>

            <div className="mt-10 p-6 rounded-2xl bg-mist border-l-4 border-teal">
              <p className="text-sm text-slate-700 leading-relaxed">
                <strong className="text-navy">Compliance note.</strong> We support providers to prepare for audit
                but we do not act as the independent external Approved Quality Auditor for providers we have consulted
                to. We do not guarantee NDIS registration, audit approval, audit outcomes, participant referrals or
                client numbers.
              </p>
            </div>

            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/contact" className="btn-primary px-6 py-3 rounded-lg font-semibold text-sm inline-flex items-center gap-2">
                Book Discovery Call <ArrowRight size={16} />
              </Link>
              <Link to="/pricing" className="btn-secondary px-6 py-3 rounded-lg font-semibold text-sm">
                View Pricing
              </Link>
            </div>
          </div>

          <aside className="lg:col-span-4">
            <div className="sticky top-28 space-y-5">
              <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-lg">
                <img src={images.office} alt="Office" loading="lazy" decoding="async" className="w-full h-44 object-cover" />
                <div className="p-5 bg-white">
                  <h4 className="text-navy font-bold">Discuss this service</h4>
                  <p className="mt-2 text-sm text-slate-600">
                    Book a free 15 minute discovery call to discuss how this service applies to your scope.
                  </p>
                  <Link to="/contact" className="mt-4 btn-primary inline-flex items-center justify-center w-full px-4 py-2.5 rounded-lg text-sm font-semibold gap-2">
                    Book Discovery Call <ArrowRight size={15} />
                  </Link>
                </div>
              </div>

              <div className="bg-navy text-white rounded-2xl p-6">
                <p className="text-xs uppercase tracking-[0.18em] text-teal-light font-bold mb-2">Other services</p>
                <div className="space-y-2 mt-3">
                  {related.map((r) => (
                    <Link key={r.slug} to={`/services/${r.slug}`} className="flex items-center justify-between gap-3 py-2.5 border-b border-white/10 last:border-0 text-sm hover:text-teal-light transition">
                      <span>{r.title}</span>
                      <ArrowRight size={14} />
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </main>
  );
}
